<script>
import validationMessages from '@/components/validations'
import {required} from "vuelidate/lib/validators"
import Monitor from '@/services/Monitor'

export default {
  components: {
    validationMessages,
  },
  props: ['configGroups', 'configKeys', 'configDefinitions'],
  data() {
    return {
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      key: '',
      group: '',
      description: '',
      keyTypes: ['string','integer','boolean','json'],
      type: 'string',
      tenant_required: 0,
      common_required: 0,
      is_secret: 0,
      csrf_token: localStorage.getItem('csrf_token')
    };
  },

  validations: {
    key: {required},
  },

  methods: {
    refreshData() {
      this.$emit('onRefresh')
    },

    closeModal() {
      this.showModal = false
    },

    resetProps() {
      this.group = ''
      this.key = ''
      this.description = ''
      this.tenant_required = 0
      this.common_required = 0
      this.is_secret = 0
      this.submitted = false
    },

    async createTenantEnvConfigDefinition() {
      this.submitted = true
      this.tryingToSubmit = true

      this.$v.$touch()
      if (this.$v.$invalid) {
        this.tryingToSubmit = false
        return
      }

      const formData = {
        group: this.group,
        key: this.key,
        type: this.type,
        description: this.description,
        tenant_required: this.tenant_required,
        common_required: this.common_required,
        is_secret: this.is_secret
      }

      try {
        const response = await Monitor.createTenantEnvConfigDefinition(formData)
        if (response.data.data) {
          this.successmsg('Config Definition Created!')
          this.refreshData()
          this.closeModal()
        } else {
          this.failedmsg(response.data.error || 'Failed to create Config Definition!')
        }
      } catch (error) {
        const resData = error.response?.data?.error || 'Failed'
        const errorMsg = Array.isArray(resData) ? resData[0] : resData
        this.failedmsg(errorMsg)
      } finally {
        this.tryingToSubmit = false
      }
    },

  },
  watch: {
    key(newVal) {
      if (newVal) {
        // Replace spaces and non-alphanumeric chars with underscore, uppercase
        this.key = newVal
            .trim()                          // remove leading/trailing spaces
            .replace(/[^a-zA-Z0-9]/g, '_')  // replace non-alphanumeric with _
            .replace(/_+/g, '_')             // collapse multiple underscores
            .toUpperCase();                  // convert to uppercase
      }
    },

  },
  computed: {
    keyExists() {
      if (!this.key) return false;
      return this.configKeys.includes(this.key);
    },
    selectedKeyDefinition() {
      if (!this.key || !this.configDefinitions) return null;

      return this.configDefinitions.find(
          item => item.key === this.key
      ) || null;
    }
  }
};
</script>

<template>
  <b-modal id="add_new_env_config_definition" size="lg" v-model="showModal" @hidden="resetProps" title="Add New Config Definition"
           title-class="font-18" :no-enforce-focus="true">
    <form @submit.prevent="createTenantEnvConfigDefinition">
      <div class="row">

        <div class="col-12">
          <h6 class="text-uppercase mb-2">Key Definition</h6>
          <small class="text-muted d-block">
            Define information for key definition.
          </small>
        </div>

        <div class="col-12">
          <div class="mb-3">
            <label class="control-label form-label">Key Name *</label>
            <small v-if="key" class="float-end">
              <span v-if="keyExists" class="text-primary">Key def exists</span>
              <span v-else class="text-primary">New key</span>
            </small>
            <b-form-input
                v-model="key"
                :class="{ 'is-invalid': submitted && $v.key.$error }"
                list="groupKeyOptions"
            >
            </b-form-input>
            <datalist id="groupKeyOptions">
              <option v-for="g in configKeys" :key="g" :value="g">{{ g }}</option>
            </datalist>
            <validationMessages v-if="submitted" :fieldName="'Key Name'" :validationName="$v.key"></validationMessages>
          </div>

          <div v-if="selectedKeyDefinition && keyExists" class="mb-3">
            <p class="mb-1">
              <strong>Type:</strong>
              {{ selectedKeyDefinition.type }}
            </p>

            <p class="mb-0">
              <strong>Description:</strong>
              {{ selectedKeyDefinition.description }}
            </p>
          </div>

          <!-- Config Group -->
          <div v-if="!keyExists" class="row">
            <div class="col-9">
              <div class="mb-3">
                <label class="control-label form-label">Config Group (optional)</label>
                <input
                    v-model="group"
                    class="form-control"
                    list="groupOptions"
                    placeholder="Select or type a group"
                >
                <datalist id="groupOptions">
                  <option v-for="g in configGroups" :key="g" :value="g">{{ g }}</option>
                </datalist>
              </div>
            </div>

            <div class="col-3">
              <div class="mb-3">
                <label class="form-label">Key Type</label>
                <b-form-select
                    v-model="type"
                    :options="keyTypes"
                    class="w-100"
                ></b-form-select>
              </div>
            </div>
          </div>

          <div v-if="!keyExists" class="mb-3">
            <label class="control-label form-label">Key Description</label>
            <b-form-input v-model="description"></b-form-input>
          </div>

          <div class="row">
            <div class="col-4">
              <b-form-checkbox-group>
                <div class="form-check form-switch form-switch-lg mb-3" >
                  <input v-model="tenant_required"
                         type="checkbox"
                         class="form-check-input"
                         id="tenant_required"
                  />
                  <label class="form-check-label" for="tenant_required">Tenant Required</label>
                </div>
              </b-form-checkbox-group>
            </div>

            <div class="col-4">
              <b-form-checkbox-group>
                <div class="form-check form-switch form-switch-lg mb-3" >
                  <input v-model="common_required"
                         type="checkbox"
                         class="form-check-input"
                         id="common_required"
                  />
                  <label class="form-check-label" for="common_required">Common Required</label>
                </div>
              </b-form-checkbox-group>
            </div>

            <div class="col-4">
              <b-form-checkbox-group>
                <div class="form-check form-switch form-switch-lg mb-3" >
                  <input v-model="is_secret"
                         type="checkbox"
                         class="form-check-input"
                         id="is_secret"
                  />
                  <label class="form-check-label" for="is_secret">Is Secret</label>
                </div>
              </b-form-checkbox-group>
            </div>
          </div>

        </div>
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="createTenantEnvConfigDefinition" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Submit
      </b-button>
    </template>
  </b-modal>
</template>